import type { Transition } from '@uirouter/react';
import { Col, Space } from 'antd';
import type { RcFile } from 'antd/lib/upload';
import { useCallback, useEffect, useState } from 'react';

import { api } from '@/api';
import type { WrappedItemsResult } from '@/api/types';
import { Drawer } from '@/components/drawer';
import { Upload } from '@/components/upload';
import type { AttachmentType } from '@/types/enums';
import { getTranslatedString } from '@/utils';
import { showError } from '@/utils/common';

import { AttachedFiles } from './attached-files';

/** Entity shape required for the attachments context (used for typing at call site only). */
export type EntityWithAttachments = {
  attachments: cyrian.api.attachments.Attachment[];
};

interface AttachmentsProps {
  transition: Transition;
  apiEntity: string;
  closeRoute: string;
  paramId: string;
  attachmentType: AttachmentType;
  isDrawerLayout?: boolean;
}

// Generic T is for call-site typing only (e.g. <Attachments<BVWithAttachment>>); not used in implementation
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- phantom type for caller
export function Attachments<T extends EntityWithAttachments>({
  transition,
  apiEntity,
  paramId,
  attachmentType,
  closeRoute,
  isDrawerLayout = true,
}: AttachmentsProps) {
  const { [paramId]: id } = transition.router.globals.params;

  const onClose = useCallback(() => {
    transition.router.stateService.go(closeRoute);
  }, [transition, closeRoute]);

  const [attachments, setAttachments] = useState<cyrian.api.attachments.Attachment[]>([]);
  useEffect(() => {
    api.attachments
      .getAll(apiEntity, id)
      .source.then(({ data }: WrappedItemsResult<cyrian.api.attachments.Attachment[]>) =>
        setAttachments(data.items || []),
      )
      .catch((err) => {
        showError(err);
        onClose();
      });
  }, [apiEntity, id, onClose]);

  const request = (fileList: RcFile[]) => {
    const formData = new FormData();
    fileList.forEach((file: RcFile) => {
      formData.append('files', file);
    });
    formData.append('objectId', id);
    formData.append('type', attachmentType);

    return api.attachments
      .attachFiles(formData)
      .source.then(({ data }: WrappedItemsResult<cyrian.api.attachments.Attachment[]>) => {
        setAttachments([...attachments, ...data.items]);
      });
  };

  const title = getTranslatedString('attachments.title', { 0: id });

  return (
    <>
      {isDrawerLayout ? (
        <Drawer onClose={onClose} visible={true} title={title}>
          <Col>
            <Upload request={request} />
          </Col>
          {Boolean(attachments.length) && <AttachedFiles attachments={attachments} />}
        </Drawer>
      ) : (
        <Space>
          <Col>
            <Upload request={request} />
            {Boolean(attachments.length) && (
              <Col>
                <AttachedFiles attachments={attachments} />
              </Col>
            )}
          </Col>
        </Space>
      )}
    </>
  );
}
